import '.h{display:grid;}.ssr-plugin'
import '.i{grid-template-columns:minmax(0, 1fr);}.ssr-plugin'
import '.j{gap:1.5rem;}.ssr-plugin'
import { BlogCard } from '@app/components/blocks/card/BlogCard'
import { useCallback, useEffect, useState } from 'react'
import { useClient, useLoading, useQuery } from '@based/sdk/react'
import { KnowledgeHubModule } from '@schema/knowledgeHubModule'
import { pages } from '@app/util/links'
import { styled } from 'inlines'
import { useTranslations } from '@i18n/useLanguage'
import { SearchField } from '@app/components/blocks/searchBar/SearchField'
import Column from '@app/components/layout/Column'
import { PageLayout } from '../layout/PageLayout'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { useTenantContext } from '@app/util/contexts/TenantContext'
import { Empty } from '@app/components/blocks/empty/Empty'
import PersonWithBinocularsImage from '@app/assets/images/person_with_binoculars.png'
import { StaticPageSeo } from '@app/components/core/seo/StaticPageSeo'

export const KnowledgeHub = () => {
  const t = useTranslations().app.knowledgeHubPage
  return (
    <PageLayout>
      <StaticPageSeo
        path={pages.knowledgeHub}
        pageTitle={t.title}
        description={t.description}
      />
      <Title.H1 style={{ textAlign: 'center', marginBottom: '0rem' }}>
        {t.title}
      </Title.H1>
      <Text.Large
        style={{
          marginBottom: '1.5rem',
          textAlign: 'center',
          maxWidth: '40rem',
          width: '100%',
          alignSelf: 'center',
        }}
      >
        {t.description}
      </Text.Large>

      <KnowledgeHubItemsList />
    </PageLayout>
  )
}

export const KnowledgeHubItemsList = () => {
  const t = useTranslations().app.knowledgeHubPage
  const [searchBarInput, setSearchBarInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [clientModules, setClientModules] = useState<KnowledgeHubModule[]>([])
  const [mounted, setMounted] = useState(false)
  const client = useClient()
  const { id: tenantId } = useTenantContext()

  const serverQuery = useQuery('list-knowledge-hub-module', {
    searchTerm: searchTerm || undefined,
    tenantId,
  })
  const serverModules: KnowledgeHubModule[] = serverQuery.data ?? []
  const isLoading = useLoading()

  const subscribeList = useCallback(() => {
    const close = client
      .query('list-knowledge-hub-module', {
        searchTerm: searchTerm || undefined,
        tenantId,
      })
      .subscribe((result) => {
        setClientModules(result ?? [])
        setMounted(true)
      })
    return () => close()
  }, [client, searchTerm, tenantId])

  useEffect(() => {
    setMounted(false)
    const cleanup = subscribeList()
    return cleanup
  }, [subscribeList])

  const modules: KnowledgeHubModule[] = mounted ? clientModules : serverModules

  return (
    <Column gap="3rem" alignItems="stretch">
      <SearchField
        placeholder={t.searchPlaceholder}
        searchBarInput={searchBarInput}
        searchTerm={searchTerm}
        setSearchBarInput={setSearchBarInput}
        setSearchTerm={setSearchTerm}
        style={{ maxWidth: '40rem', width: '100%', alignSelf: 'center' }}
      />
      {isLoading ? (
        <styled.div>{t.loading}</styled.div>
      ) : (
        <>
          <styled.div className="h i j"
            style={{
              '@media (min-width: 800px)': {
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                '& > :nth-child(1)': {
                  gridColumn: modules.length > 3 ? 'span 2' : 'span 1',
                },
              },
            }}
          >
            {modules.map((item: KnowledgeHubModule) => (
              <BlogCard
                key={item.id}
                title={item.title}
                intro={item.intro}
                image={item.images?.[0]}
                to={pages.knowledgeHubModule(item.slug)}
              />
            ))}
          </styled.div>

          {modules.length === 0 && searchTerm && (
            <Empty
              title={t.noArticlesTitle}
              imageSrc={PersonWithBinocularsImage}
              style={{ marginTop: '4rem' }}
            >
              {t.noArticlesDescription}
            </Empty>
          )}
        </>
      )}
    </Column>
  )
}
